import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Header } from '../Header/Header';
import { HeroTitleSvg } from '../Hero/assets/HeroTitleSvg';
import { heroContent } from '../Hero/Hero.content';
import {
  getScrollVideoState,
  preloadScrollVideo,
  subscribeScrollVideo,
} from '../ScrollVideo/ScrollVideo.preload';
import styles from './Preloader.module.sass';

// Предел ожидания видео сверх основной анимации прелоадера (страховка от медленной сети)
const VIDEO_MAX_WAIT = 20000;

export function Preloader({ onFadeStart }) {
  const [progress, setProgress] = useState(0);
  const [fading, setFading] = useState(false);
  const [done, setDone] = useState(false);
  const rafRef = useRef(null);
  const videoProgressRef = useRef(getScrollVideoState().progress);
  const videoDoneRef = useRef(getScrollVideoState().done);
  const onFadeStartRef = useRef(onFadeStart);
  onFadeStartRef.current = onFadeStart;

  // Качаем видео scroll-секции целиком, пока крутится прелоадер
  useEffect(() => {
    const unsubscribe = subscribeScrollVideo((videoState) => {
      videoProgressRef.current = videoState.progress;
      videoDoneRef.current = videoState.done;
    });

    preloadScrollVideo();

    return unsubscribe;
  }, []);

  useEffect(() => {
    const duration = 2200;
    const start = performance.now();

    const tick = (now) => {
      const elapsed = now - start;
      const timeProgress = Math.min(100, Math.round((elapsed / duration) * 100));
      const videoProgress = Math.round(videoProgressRef.current * 100);
      const expired = elapsed > duration + VIDEO_MAX_WAIT;

      // Шкала идёт по медленному из двух: анимация и реальная загрузка видео
      const p = expired ? timeProgress : Math.min(timeProgress, videoProgress);
      setProgress(p);

      const ready = (videoDoneRef.current && timeProgress >= 100) || expired;

      if (!ready) {
        rafRef.current = requestAnimationFrame(tick);
      } else {
        setProgress(100);
        setFading(true);
        onFadeStartRef.current?.();
      }
    };

    rafRef.current = requestAnimationFrame(tick);

    return () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  useEffect(() => {
    if (!fading) return;
    const t = setTimeout(() => setDone(true), 1500);
    return () => clearTimeout(t);
  }, [fading]);

  useLayoutEffect(() => {
    const { documentElement: html, body } = document;
    if (fading) {
      html.style.overflow = '';
      body.style.overflow = '';
      return undefined;
    }
    html.style.overflow = '';
    body.style.overflow = '';
    return () => {
      html.style.overflow = '';
      body.style.overflow = '';
    };
  }, [fading]);

  if (done) return null;

  return (
    <div className={`${styles.preloader}${fading ? ` ${styles['preloader--fading']}` : ''}`}>
      <div className={styles['preloader__header']}>
        <Header />
      </div>
      <div className={styles['preloader__body']}>
        <h1 className={styles['preloader__title']} aria-hidden="true">
          <HeroTitleSvg shimmer />
        </h1>
        <div className={styles['preloader__caption']}>
          <p
            className={styles['preloader__rightTitle']}
            dangerouslySetInnerHTML={{ __html: heroContent.rightTitle }}
          />
          <p
            className={styles['preloader__rightText']}
            dangerouslySetInnerHTML={{ __html: heroContent.rightText }}
          />
        </div>
      </div>
      <div className={styles['preloader__progress']}>
        <div className={styles['preloader__track']}>
          <div className={styles['preloader__bar']} style={{ width: `${progress}%` }} />
        </div>
        <span className={styles['preloader__percent']}>{progress}%</span>
      </div>
    </div>
  );
}
